import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Select, Checkbox, Tag } from 'antd';
import {
  fetchAllCouriers,
  selectCouriersState,
} from '@/redux-toolkit/slice/onboardingSlice';
import {
  fetchTeamsDDL,
  selectTeamsState,
} from '@/redux-toolkit/slice/teamsSlice';
import type { CompanyTeam } from '@/apis/settings';
import useToken from '@/common/helper/useToken';
import type { AppDispatch } from '@/redux-toolkit/store';
import TeamsSelector from '@/components/TeamsSelector';
import useFetchLocations from '@/hooks/useFetchLocations';
import { useTeamGroups } from '@/hooks/useTeamGroups';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';

const { Option } = Select;

/**
 * Customization UI for INBOUND_EVENT_TRIGGER nodes.
 */
export const InboundEventTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { getToken } = useToken();
  const courierState = useSelector(selectCouriersState);
  const teamsState = useSelector(selectTeamsState);
  const { locations, loading: locationsLoading } = useFetchLocations();
  // Team groups expand to their teams in the team filter.
  const teamGroups = useTeamGroups(teamsState.list.map((team) => team.id));

  const currentCourierIds = nodeData.inbound_event_trigger?.courier_ids || [];
  const [selectedCourierIds, setSelectedCourierIds] =
    useState<string[]>(currentCourierIds);

  const [selectedTeams, setSelectedTeams] = useState<CompanyTeam[]>([]);
  const [selectedOriginIds, setSelectedOriginIds] = useState<string[]>([]);
  const [selectedDestinationIds, setSelectedDestinationIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    const token = getToken();
    if (token && courierState.courierList.length === 0) {
      // Fire and forget: the thunk settles with a rejected action, never throws.
      const _ = dispatch(fetchAllCouriers(token));
    }
  }, [dispatch, getToken, courierState.courierList.length]);

  useEffect(() => {
    const token = getToken();
    if (token && teamsState.list.length === 0) {
      // Fire and forget: the thunk settles with a rejected action, never throws.
      const _ = dispatch(fetchTeamsDDL(token));
    }
  }, [dispatch, getToken, teamsState.list.length]);

  useEffect(() => {
    const courierIds = nodeData.inbound_event_trigger?.courier_ids || [];
    setSelectedCourierIds(courierIds);

    const teamIds = nodeData.inbound_event_trigger?.team_ids || [];
    const teams = teamIds
      .map((id) => teamsState.list.find((team) => team.id === id))
      .filter((team): team is CompanyTeam => team !== undefined);
    setSelectedTeams(teams);

    const originIds = nodeData.inbound_event_trigger?.origin_ids || [];
    setSelectedOriginIds(originIds);

    const destinationIds =
      nodeData.inbound_event_trigger?.destination_ids || [];
    setSelectedDestinationIds(destinationIds);
  }, [nodeData, teamsState.list]);

  const buildTriggerData = (overrides: {
    courier_ids?: string[];
    team_ids?: string[];
    origin_ids?: string[];
    destination_ids?: string[];
  }) => ({
    courier_ids: overrides.courier_ids ?? selectedCourierIds,
    team_ids: overrides.team_ids ?? selectedTeams.map((team) => team.id),
    origin_ids: overrides.origin_ids ?? selectedOriginIds,
    destination_ids: overrides.destination_ids ?? selectedDestinationIds,
  });

  const handleCourierChange = (values: string[]) => {
    let updatedCourierIds: string[];

    if (values.includes('ALL')) {
      if (selectedCourierIds.length === availableCouriers.length) {
        updatedCourierIds = [];
      } else {
        updatedCourierIds = availableCouriers.map((c) => c.id);
      }
    } else {
      updatedCourierIds = values.filter((v) => v !== 'ALL');
    }

    setSelectedCourierIds(updatedCourierIds);
    onChange({
      inbound_event_trigger: buildTriggerData({
        courier_ids: updatedCourierIds,
      }),
    });
  };

  const handleTeamsChange = (teams: CompanyTeam[]) => {
    setSelectedTeams(teams);
    onChange({
      inbound_event_trigger: buildTriggerData({
        team_ids: teams.map((team) => team.id),
      }),
    });
  };

  const handleOriginsChange = (values: string[]) => {
    let updatedOriginIds: string[];

    if (values.includes('ALL')) {
      if (selectedOriginIds.length === locations.length) {
        updatedOriginIds = [];
      } else {
        updatedOriginIds = locations.map((loc) => loc.id);
      }
    } else {
      updatedOriginIds = values.filter((v) => v !== 'ALL');
    }

    setSelectedOriginIds(updatedOriginIds);
    onChange({
      inbound_event_trigger: buildTriggerData({ origin_ids: updatedOriginIds }),
    });
  };

  const handleDestinationsChange = (values: string[]) => {
    let updatedDestinationIds: string[];

    if (values.includes('ALL')) {
      if (selectedDestinationIds.length === locations.length) {
        updatedDestinationIds = [];
      } else {
        updatedDestinationIds = locations.map((loc) => loc.id);
      }
    } else {
      updatedDestinationIds = values.filter((v) => v !== 'ALL');
    }

    setSelectedDestinationIds(updatedDestinationIds);
    onChange({
      inbound_event_trigger: buildTriggerData({
        destination_ids: updatedDestinationIds,
      }),
    });
  };

  const availableCouriers = courierState.courierList.filter(
    (courier) => courier.is_enabled
  );

  const couriersLoading = courierState.status === 'loading';
  const teamsLoading = teamsState.loading;

  return (
    <>
      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Select Couriers</label>
        {couriersLoading ? (
          <div>Loading couriers...</div>
        ) : availableCouriers.length === 0 ? (
          <div>No couriers available</div>
        ) : (
          <Select
            mode='multiple'
            placeholder='Select couriers...'
            value={selectedCourierIds}
            onChange={handleCourierChange}
            className={styles.filterDropdown}
            style={{
              background: 'var(--light-gray)',
              border: 'none',
              boxShadow: 'none',
            }}
            optionLabelProp='label'
            disabled={isReadOnly}
            tagRender={({ label, value }) => {
              if (selectedCourierIds.length === availableCouriers.length) {
                if (value === selectedCourierIds[0]) {
                  return (
                    <Tag className={styles.filterTag} closable>
                      All
                    </Tag>
                  );
                }
                // One "All" tag stands in for every selected value.
                return <></>;
              }
              return (
                <Tag className={styles.filterTag} closable>
                  {label}
                </Tag>
              );
            }}
          >
            <Option key='all' value='ALL' label='All'>
              <Checkbox
                className={styles.filterCheckbox}
                checked={selectedCourierIds.length === availableCouriers.length}
                indeterminate={
                  selectedCourierIds.length > 0 &&
                  selectedCourierIds.length < availableCouriers.length
                }
              >
                All
              </Checkbox>
            </Option>
            {availableCouriers.map((courier) => (
              <Option key={courier.id} value={courier.id} label={courier.title}>
                <Checkbox
                  className={styles.filterCheckbox}
                  checked={selectedCourierIds.includes(courier.id)}
                >
                  {courier.title}
                </Checkbox>
              </Option>
            ))}
          </Select>
        )}
      </div>

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Select Teams</label>
        {teamsLoading ? (
          <div>Loading teams...</div>
        ) : teamsState.list.length === 0 ? (
          <div>No teams available</div>
        ) : (
          <TeamsSelector
            teamsOptions={teamsState.list}
            selectedTeams={selectedTeams}
            onChange={handleTeamsChange}
            singleSelect={false}
            disabled={isReadOnly}
            teamGroups={teamGroups}
          />
        )}
      </div>

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Select Origins</label>
        {locationsLoading ? (
          <div>Loading locations...</div>
        ) : locations.length === 0 ? (
          <div>No locations available</div>
        ) : (
          <Select
            mode='multiple'
            placeholder='Select origins...'
            value={selectedOriginIds}
            onChange={handleOriginsChange}
            className={styles.filterDropdown}
            style={{
              background: 'var(--light-gray)',
              border: 'none',
              boxShadow: 'none',
            }}
            optionLabelProp='label'
            disabled={isReadOnly}
            tagRender={({ label, value }) => {
              if (selectedOriginIds.length === locations.length) {
                if (value === selectedOriginIds[0]) {
                  return (
                    <Tag className={styles.filterTag} closable>
                      All
                    </Tag>
                  );
                }
                // One "All" tag stands in for every selected value.
                return <></>;
              }
              return (
                <Tag className={styles.filterTag} closable>
                  {label}
                </Tag>
              );
            }}
          >
            <Option key='all-origins' value='ALL' label='All'>
              <Checkbox
                className={styles.filterCheckbox}
                checked={selectedOriginIds.length === locations.length}
                indeterminate={
                  selectedOriginIds.length > 0 &&
                  selectedOriginIds.length < locations.length
                }
              >
                All
              </Checkbox>
            </Option>
            {locations.map((location) => (
              <Option
                key={location.id}
                value={location.id}
                label={location.name}
              >
                <Checkbox
                  className={styles.filterCheckbox}
                  checked={selectedOriginIds.includes(location.id)}
                >
                  {location.name}
                </Checkbox>
              </Option>
            ))}
          </Select>
        )}
      </div>

      <div className={styles.customizationSection}>
        <label className={styles.sectionLabel}>Select Destinations</label>
        {locationsLoading ? (
          <div>Loading locations...</div>
        ) : locations.length === 0 ? (
          <div>No locations available</div>
        ) : (
          <Select
            mode='multiple'
            placeholder='Select destinations...'
            value={selectedDestinationIds}
            onChange={handleDestinationsChange}
            className={styles.filterDropdown}
            style={{
              background: 'var(--light-gray)',
              border: 'none',
              boxShadow: 'none',
            }}
            optionLabelProp='label'
            disabled={isReadOnly}
            tagRender={({ label, value }) => {
              if (selectedDestinationIds.length === locations.length) {
                if (value === selectedDestinationIds[0]) {
                  return (
                    <Tag className={styles.filterTag} closable>
                      All
                    </Tag>
                  );
                }
                // One "All" tag stands in for every selected value.
                return <></>;
              }
              return (
                <Tag className={styles.filterTag} closable>
                  {label}
                </Tag>
              );
            }}
          >
            <Option key='all-destinations' value='ALL' label='All'>
              <Checkbox
                className={styles.filterCheckbox}
                checked={selectedDestinationIds.length === locations.length}
                indeterminate={
                  selectedDestinationIds.length > 0 &&
                  selectedDestinationIds.length < locations.length
                }
              >
                All
              </Checkbox>
            </Option>
            {locations.map((location) => (
              <Option
                key={location.id}
                value={location.id}
                label={location.name}
              >
                <Checkbox
                  className={styles.filterCheckbox}
                  checked={selectedDestinationIds.includes(location.id)}
                >
                  {location.name}
                </Checkbox>
              </Option>
            ))}
          </Select>
        )}
      </div>
    </>
  );
};
